/* eslint react/jsx-key: off */
import React from 'react';
import {
  Edit,
  SimpleForm,
  TextInput,
  required,
  NumberInput,
  Toolbar,
  SaveButton,
  FormDataConsumer
} from 'react-admin';
import {
  withStyles,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Box,
  Divider,
  Avatar
} from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import CategoryIcon from '@material-ui/icons/Category';
import SettingsIcon from '@material-ui/icons/Settings';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import BackButton from '../common/BackButton';

const customStyles = () => ({
  container: {
    padding: '8px'
  },
  headerCard: {
    background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #334155 100%)',
    color: '#FFFFFF',
    borderRadius: '10px',
    padding: '10px 16px',
    marginBottom: '10px',
    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.25)'
  },
  headerTitle: {
    fontWeight: 800,
    fontSize: '1.25rem',
    color: '#FFFFFF',
    lineHeight: 1.2
  },
  headerSubtitle: {
    color: '#F1F5F9',
    fontSize: '0.85rem',
    marginTop: '2px',
    fontWeight: 600
  },
  sectionCard: {
    borderRadius: '8px',
    border: '1.5px solid #CBD5E1',
    boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
    marginBottom: '8px',
    backgroundColor: '#FFFFFF',
    overflow: 'hidden',
    height: '100%'
  },
  sectionHeader: {
    backgroundColor: '#F1F5F9',
    borderBottom: '1.5px solid #CBD5E1',
    padding: '6px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  sectionTitle: {
    fontWeight: 800,
    fontSize: '0.95rem',
    color: '#000000'
  },
  sectionContent: {
    padding: '10px 12px !important'
  },
  inputFullWidth: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: '6px',
      backgroundColor: '#FFFFFF',
      borderColor: '#94A3B8'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#94A3B8 !important'
    },
    '& .MuiInputBase-input': {
      padding: '8px 10px',
      fontSize: '0.875rem',
      color: '#000000 !important',
      WebkitTextFillColor: '#000000 !important',
      fontWeight: 400
    },
    '& .MuiInputBase-input.Mui-disabled': {
      color: '#000000 !important',
      WebkitTextFillColor: '#000000 !important',
      fontWeight: 400,
      opacity: 1
    },
    '& .MuiFormLabel-root': {
      color: '#000000 !important',
      fontWeight: 800,
      backgroundColor: '#FFFFFF',
      padding: '0 4px',
      fontSize: '0.8rem',
      whiteSpace: 'nowrap',
      maxWidth: 'calc(100% - 20px)',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    '& .MuiFormLabel-root.Mui-disabled': {
      color: '#0F172A !important',
      fontWeight: 800,
      backgroundColor: '#FFFFFF',
      padding: '0 4px',
      fontSize: '0.8rem',
      whiteSpace: 'nowrap',
      maxWidth: 'calc(100% - 20px)',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }
  },
  noteCard: {
    padding: '12px 14px',
    backgroundColor: '#F0F9FF',
    border: '1.5px solid #0284C7',
    borderRadius: '8px',
    width: '100%',
    boxSizing: 'border-box'
  }
});

const toolbarStyles = {
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px 16px',
    backgroundColor: '#FAF5FF',
    borderTop: '1px solid #E9D5FF',
    borderRadius: '0 0 10px 10px'
  }
};

const ServiceCategoryEditToolbar = withStyles(toolbarStyles)((props) => (
  <Toolbar {...props}>
    <SaveButton variant="contained" color="primary" size="small" />
    <BackButton />
  </Toolbar>
));

const ServiceCategoryEditContent = ({classes, ...props}) => {
  return (
    <FormDataConsumer>
      {({ formData }) => {
        const record = formData || props.record || {};
        return (
          <Box className={classes.container}>
            {/* Grid 2 Cards */}
            <Grid container spacing={1} style={{marginBottom: 8}}>
              {/* Card 1: Category Info */}
              <Grid item xs={12} md={6}>
                <Card className={classes.sectionCard}>
                  <Box className={classes.sectionHeader}>
                    <Avatar style={{backgroundColor: '#EEF2FF', color: '#4F46E5', width: 26, height: 26}}>
                      <SettingsIcon style={{fontSize: 16}} />
                    </Avatar>
                    <Typography className={classes.sectionTitle}>1. Category Configuration</Typography>
                  </Box>
                  <CardContent className={classes.sectionContent}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <TextInput
                          disabled
                          source="serviceCategoryName"
                          label="Service Category Name"
                          InputLabelProps={{shrink: true}}
                          className={classes.inputFullWidth}
                          variant="outlined"
                          margin="dense"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextInput
                          source="serviceCode"
                          label="Service Code"
                          InputLabelProps={{shrink: true}}
                          className={classes.inputFullWidth}
                          variant="outlined"
                          margin="dense"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <NumberInput
                          source="profitPercentage"
                          label="Profit Percentage (%)"
                          validate={required()}
                          InputLabelProps={{shrink: true}}
                          className={classes.inputFullWidth}
                          variant="outlined"
                          margin="dense"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Card 2: Commission Threshold & Limits */}
              <Grid item xs={12} md={6}>
                <Card className={classes.sectionCard} style={{border: '1.5px solid #0284C7'}}>
                  <Box className={classes.sectionHeader} style={{backgroundColor: '#F0F9FF', borderBottom: '1.5px solid #BAE6FD'}}>
                    <Avatar style={{backgroundColor: '#0284C7', color: '#FFF', width: 26, height: 26}}>
                      <MonetizationOnIcon style={{fontSize: 16}} />
                    </Avatar>
                    <Typography className={classes.sectionTitle} style={{color: '#0369A1'}}>
                      2. Commission Thresholds & Rules
                    </Typography>
                  </Box>
                  <CardContent className={classes.sectionContent}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <NumberInput
                          source="commissionThreshold"
                          label="Commission Threshold Amount (₹)"
                          InputLabelProps={{shrink: true}}
                          className={classes.inputFullWidth}
                          variant="outlined"
                          margin="dense"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <NumberInput
                          source="fixedCommission"
                          label="Fixed Commission Above Threshold (₹)"
                          InputLabelProps={{shrink: true}}
                          className={classes.inputFullWidth}
                          variant="outlined"
                          margin="dense"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* N.B. Note Box */}
            <Paper elevation={0} className={classes.noteCard}>
              <Box style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6}}>
                <InfoIcon style={{color: '#0284C7', fontSize: 20}} />
                <Typography variant="subtitle2" style={{fontWeight: 800, color: '#0369A1'}}>
                  N.B. (Commission Threshold & Fixed Commission Rules):
                </Typography>
              </Box>
              <Divider style={{marginBottom: 8, backgroundColor: '#BAE6FD'}} />
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" style={{color: '#0C4A6E', fontSize: '0.8rem', lineHeight: 1.4}}>
                    &bull; <strong>Below Threshold (&le; Limit):</strong> Standard <em>Profit Percentage (%)</em> applies.
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" style={{color: '#0C4A6E', fontSize: '0.8rem', lineHeight: 1.4}}>
                    &bull; <strong>Above Threshold (&gt; Limit):</strong> Flat <em>Fixed Commission</em> applies (capped at quotation value).
                  </Typography>
                </Grid>
              </Grid>

              {/* Scenarios List */}
              <Box
                style={{
                  backgroundColor: '#FFFFFF',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  border: '1px solid #7DD3FC',
                  marginTop: '8px'
                }}
              >
                <Typography variant="caption" style={{fontWeight: 800, color: '#0369A1', display: 'block', marginBottom: 4, fontSize: '0.775rem'}}>
                  Example Scenarios across All Cases:
                </Typography>

                {/* Scenario A */}
                <Typography variant="caption" style={{color: '#0F172A', fontWeight: 700, display: 'block', marginTop: 4, fontSize: '0.75rem'}}>
                  Case 1: Threshold = ₹100,000 &amp; Fixed Commission = ₹10,000 (Profit = 10%)
                </Typography>
                <Typography variant="caption" style={{color: '#059669', display: 'block', marginLeft: 10, fontSize: '0.75rem'}}>
                  &bull; Job Amount = ₹50,000 (&le; ₹100,000): 10% percentage commission applies = <strong>₹5,000</strong>.
                </Typography>
                <Typography variant="caption" style={{color: '#2563EB', display: 'block', marginLeft: 10, fontSize: '0.75rem'}}>
                  &bull; Job Amount = ₹250,000 (&gt; ₹100,000): Flat fixed commission applies = <strong>₹10,000</strong> (instead of 10% = ₹25,000).
                </Typography>

                {/* Scenario B */}
                <Typography variant="caption" style={{color: '#0F172A', fontWeight: 700, display: 'block', marginTop: 6, fontSize: '0.75rem'}}>
                  Case 2: Threshold = ₹3,000 &amp; Fixed Commission = ₹30,000 (Profit = 10%)
                </Typography>
                <Typography variant="caption" style={{color: '#059669', display: 'block', marginLeft: 10, fontSize: '0.75rem'}}>
                  &bull; Job Amount = ₹2,000 (&le; ₹3,000): 10% percentage commission applies = <strong>₹200</strong>.
                </Typography>
                <Typography variant="caption" style={{color: '#2563EB', display: 'block', marginLeft: 10, fontSize: '0.75rem'}}>
                  &bull; Job Amount = ₹35,000 (&gt; ₹3,000 &amp; &ge; ₹30,000): Flat fixed commission applies = <strong>₹30,000</strong> (Vendor receives ₹5,000).
                </Typography>
                <Typography variant="caption" style={{color: '#D97706', display: 'block', marginLeft: 10, fontSize: '0.75rem'}}>
                  &bull; Job Amount = ₹25,000 (&gt; ₹3,000 but &lt; ₹30,000): Commission capped at job price = <strong>₹25,000</strong> (Vendor receives ₹0).
                </Typography>
              </Box>
            </Paper>
          </Box>
        );
      }}
    </FormDataConsumer>
  );
};

const ServiceCategoryEdit = ({classes, ...props}) => (
  <Edit title="Edit Service Category" {...props} actions={false}>
    <SimpleForm toolbar={<ServiceCategoryEditToolbar />}>
      <ServiceCategoryEditContent classes={classes} />
    </SimpleForm>
  </Edit>
);

ServiceCategoryEdit.propTypes = {};

export default withStyles(customStyles)(ServiceCategoryEdit);
